import { sessionRequests } from './sessionRequests';
import { SERVICE_TERMS_VERSION } from '../../../../supabase/functions/_shared/legalTermsVersions';

const STORAGE_KEY = 'atrivial:google-signup-terms:v1';
const EXPIRY_MS = 30 * 60 * 1000;
type PendingAcceptance = { version: string; startedAt: number; userId?: string };
let inMemoryAcceptance: string | null = null;
const saves = new Map<string, Promise<void>>();

function readPending(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY) ?? inMemoryAcceptance;
  } catch {
    return inMemoryAcceptance;
  }
}

function writePending(value: string): void {
  inMemoryAcceptance = value;
  try {
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* Embedded authentication can retain the choice without browser storage. */
  }
}

/** The caller displays the terms beside the Google action before calling this. */
export function beginGoogleSignupTermsAcceptance(): void {
  writePending(JSON.stringify({ version: SERVICE_TERMS_VERSION, startedAt: Date.now() }));
}

export function cancelGoogleSignupTermsAcceptance(): void {
  inMemoryAcceptance = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* No durable value exists when storage is unavailable. */
  }
}

/** Save only after session verification, outside Supabase's synchronous auth callback. */
export async function completeGoogleSignupTermsAcceptance(userId: string): Promise<void> {
  const raw = readPending();
  if (!raw) return;
  let pending: PendingAcceptance;
  try {
    pending = JSON.parse(raw) as PendingAcceptance;
    if (
      !pending ||
      pending.version !== SERVICE_TERMS_VERSION ||
      typeof pending.startedAt !== 'number' ||
      !Number.isFinite(pending.startedAt) ||
      pending.startedAt > Date.now() ||
      Date.now() - pending.startedAt > EXPIRY_MS ||
      (pending.userId !== undefined && pending.userId !== userId)
    ) {
      cancelGoogleSignupTermsAcceptance();
      return;
    }
  } catch {
    cancelGoogleSignupTermsAcceptance();
    return;
  }

  const bound = JSON.stringify({ ...pending, userId });
  writePending(bound);
  const currentSave = saves.get(bound);
  if (currentSave) return currentSave;
  const assertOwner = sessionRequests.captureOwner();
  const save = (async () => {
    const { recordGoogleSignupTerms } = await import('@/lib/legalAcceptance');
    assertOwner();
    if (readPending() !== bound) return;
    await recordGoogleSignupTerms(userId, pending.version);
    if (readPending() === bound) cancelGoogleSignupTermsAcceptance();
  })();
  saves.set(bound, save);
  try {
    await save;
  } finally {
    if (saves.get(bound) === save) saves.delete(bound);
  }
}
